import { Component, inject } from '@angular/core';
import { AppConfig } from '@app/core/app.config';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';

/**
 * Renderer used to replace text cell values by pictograms.
 */
@Component({
    selector: 'pictogram-cell-renderer',
    templateUrl: './pictogram-cell-renderer.component.html',
    styleUrls: ['./pictogram-cell-renderer.component.scss'],
    standalone: false
})
export class PictogramCellRendererComponent implements ICellRendererAngularComp {
  protected appConfig = inject(AppConfig);

  /**
   * Value to render
   */
  public cellValue: string;

  /**
   * Pictogram to render
   */
  public pictogram: string;

  /**
   * List of pictograms
   */
  public pictograms: {value: string, image: string}[];

  /**
   * True indicates that the value should be displayed in addition of pictograms,
   * otherwise the text is hidden
   */
  public withText: boolean;

  /**
   * True indicates that the column rendered is a group,
   * otherwise it's not a group
   */
  public isGroup: boolean;

  /**
   * Called by AG-Grid once for initialization.
   * @param params Params from AG-Grid.
   */
  public agInit(params: ICellRendererParams): void {
    this.pictograms = params?.['values'] ?? [];
    this.withText = params?.['withText'] ?? false;
    this.cellValue = this.getValueToDisplay(params);
    this.pictogram = this.getPictogram(this.cellValue);
    this.isGroup = params?.['group'] ?? false;
  }

  /**
   * Retrieves the value to render.
   * @param params Params from AG-Grid.
   * @returns The value to render.
   */
  public getValueToDisplay(params: ICellRendererParams): string {
    return params.valueFormatted ? params.valueFormatted : params.value;
  }

  /**
   * Retrieves the file name of the pictogram associated with the specified value.
   * @param value The value corresponding to a pictogram.
   * @returns The file name of the associated pictogram, or undefined if not found.
   */
  public getPictogram(value: string): string | undefined {
    return this.pictograms.find(p => p.value === value)?.image;
  }

  /**
   * Used to refresh the value rendered.
   * @param params Params from AG-Grid.
   * @returns The refreshed value to render.
   */
  public refresh(params: ICellRendererParams): boolean {
    this.cellValue = this.getValueToDisplay(params);
    this.pictogram = this.getPictogram(this.cellValue);
    return true;
  }
}
